// frontend/src/pages/Dashboard/Author/CreateEditArticle.js

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../../../context/AuthContext';
import axiosInstance from '../../../utils/axiosConfig';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

function groupCategories(rawCats) {
  const map = {};
  rawCats.forEach((c) => {
    map[c._id] = { ...c, children: [] };
  });

  const topLevel = [];
  rawCats.forEach((c) => {
    if (!c.parent) {
      topLevel.push(map[c._id]);
    } else {
      const pid = c.parent._id || c.parent;
      if (map[pid]) {
        map[pid].children.push(map[c._id]);
      }
    }
  });
  return topLevel;
}

function buildCategoryOptions(rawCats) {
  const topLevel = groupCategories(rawCats);
  const result = [];

  function traverse(catNode, prefix = '') {
    result.push({ slug: catNode.slug, label: prefix + catNode.name });
    if (catNode.children && catNode.children.length > 0) {
      catNode.children.forEach((child) => traverse(child, prefix + '— '));
    }
  }
  topLevel.forEach((cat) => traverse(cat));
  return result;
}

// Build the base server URL from .env
const rawBase = process.env.REACT_APP_API_BASE_URL || 'https://entecity.ch/api';
const BASE_SERVER_URL = rawBase.replace(/\/api\/?$/, '');

// Suppress Quill warnings
const originalError = console.error;
console.error = (...args) => {
  if (/DOMNodeInserted/.test(args[0]) || /findDOMNode is deprecated/.test(args[0])) {
    return;
  }
  originalError(...args);
};

const CreateEditArticle = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [content, setContent] = useState('');
  const [mediaPaths, setMediaPaths] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [indentedCats, setIndentedCats] = useState([]);
  const [isPublished, setIsPublished] = useState(false); // Admin usage
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = user?.role === 'Admin';
  const isEditor = user?.role === 'Editor';
  const isAuthor = user?.role === 'Author';

  useEffect(() => {
    (async () => {
      try {
        const url = isAdmin ? '/admin/categories' : '/categories';
        const { data } = await axiosInstance.get(url);
        setAllCategories(data);
        const options = buildCategoryOptions(data);
        setIndentedCats(options);
      } catch {
        setError('Fehler: Kategorien konnten nicht geladen werden.');
      }
    })();
  }, [isAdmin]);

  // Falls wir bearbeiten (id existiert) => bestehenden Artikel laden
  useEffect(() => {
    if (!id) return;
    let fetchUrl = '';
    if (isAdmin) fetchUrl = '/admin/articles';
    else if (isEditor) fetchUrl = '/editor/articles';
    else if (isAuthor) fetchUrl = '/author-articles/my';
    else {
      setError('Keine Berechtigung, um Artikel zu bearbeiten.');
      return;
    }

    (async () => {
      try {
        const { data } = await axiosInstance.get(fetchUrl);
        const found = data.find((art) => art._id === id);
        if (!found) {
          setError('Artikel nicht gefunden oder keine Zugriffsrechte.');
          return;
        }
        setTitle(found.title);
        setCategory(found.category);
        setTags(found.tags.join(', '));
        setContent(found.content);
        setMediaPaths([
          ...(found.media?.images || []),
          ...(found.media?.videos || [])
        ]);
        if (isAdmin && found.isPublished) {
          setIsPublished(true);
        }
      } catch {
        setError('Fehler: Artikel konnte nicht geladen werden.');
      }
    })();
  }, [id, isAdmin, isEditor, isAuthor]);

  // Upload
  const handleUploadMedia = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('media', file);
      }
      const { data } = await axiosInstance.post('/upload', formData);
      setMediaPaths((prev) => [...prev, ...data.filePaths]);
    } catch {
      setError('Fehler: Medien-Upload fehlgeschlagen.');
    }
  };

  // Media entfernen
  const handleRemoveMedia = async (filePath) => {
    try {
      await axiosInstance.delete('/upload', {
        data: { path: filePath }
      });
      setMediaPaths((prev) => prev.filter((p) => p !== filePath));
    } catch {
      setError('Fehler: Medium konnte nicht entfernt werden.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      setError('Titel und Inhalt sind erforderlich.');
      return;
    }
    setError('');
    setSuccess('');

    // Bilder vs Videos separieren
    const images = mediaPaths.filter((p) =>
      p.match(/\.(jpg|jpeg|png|gif)$/i)
    );
    const videos = mediaPaths.filter((p) =>
      p.match(/\.(mp4|mov|avi)$/i)
    );

    const articleData = {
      title: title.trim(),
      category,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      content,
      media: { images, videos }
    };

    // Admin => publish/unpublish
    if (isAdmin) {
      articleData.isPublished = isPublished;
    }

    let baseUrl = '';
    if (isAdmin) baseUrl = '/admin/articles';
    else if (isEditor) baseUrl = '/editor/articles';
    else if (isAuthor) baseUrl = '/author-articles';
    else {
      setError('Keine Berechtigung, um Artikel zu erstellen/zu bearbeiten.');
      return;
    }

    try {
      if (!id) {
        await axiosInstance.post(baseUrl, articleData);
        setSuccess('Artikel erfolgreich erstellt.');
      } else {
        await axiosInstance.put(`${baseUrl}/${id}`, articleData);
        setSuccess('Artikel erfolgreich aktualisiert.');
      }
      // nach kurzer Zeit zurück zur Liste
      setTimeout(() => {
        if (isAdmin) navigate('/admin/manage-articles');
        else if (isEditor) navigate('/editor/manage-articles');
        else if (isAuthor) navigate('/author/my-articles');
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern des Artikels.');
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* SIDEBAR */}
      <div
        className={`${sidebarOpen ? 'block' : 'hidden'} sm:block bg-gray-100 w-64 p-4`}
      >
        {/* Schliessen-Button (mobil) */}
        <button onClick={() => setSidebarOpen(false)} className="sm:hidden mb-4">
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>

        {/* Zurück-Button */}
        <button
          onClick={() => navigate(-1)}
          className="bg-primary text-white w-full text-left px-4 py-2 rounded hover:bg-orange-600 mb-2"
        >
          ← Zurück
        </button>

        {/* Author / Editor / Admin Links */}
        {isAuthor && (
          <>
            <Link
              to="/author/dashboard"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Autor-Dashboard
            </Link>
            <Link
              to="/author/my-articles"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Meine Artikel
            </Link>
          </>
        )}

        {isEditor && (
          <>
            <Link
              to="/editor/dashboard"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Editor-Dashboard
            </Link>
            <Link
              to="/editor/manage-articles"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Artikel verwalten
            </Link>
          </>
        )}

        {isAdmin && (
          <>
            <Link
              to="/admin/dashboard"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Admin-Dashboard
            </Link>
            <Link
              to="/admin/manage-articles"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Artikel verwalten
            </Link>
            <Link
              to="/admin/manage-users"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Benutzer verwalten
            </Link>
            <Link
              to="/admin/manage-comments"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 mb-2"
            >
              Kommentare verwalten
            </Link>
            <Link
              to="/admin/manage-categories"
              className="block bg-primary text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Kategorien verwalten
            </Link>
          </>
        )}
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 p-4">
        {/* Menü öffnen (mobil) */}
        <button onClick={() => setSidebarOpen(true)} className="sm:hidden mb-4">
          <Bars3Icon className="w-6 h-6 text-gray-600" />
        </button>

        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-heading text-primary mb-6">
            {id ? 'Artikel bearbeiten' : 'Neuen Artikel erstellen'}
          </h1>

          {error && <div className="bg-red-100 text-red-700 p-2 mb-4">{error}</div>}
          {success && <div className="bg-green-100 text-green-700 p-2 mb-4">{success}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Titel */}
            <div>
              <label className="block mb-1 font-semibold">Titel</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* Kategorie */}
            <div>
              <label className="block mb-1 font-semibold">Kategorie / Unterkategorie</label>
              <select
                className="w-full p-2 border rounded"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">-- Kategorie auswählen --</option>
                {indentedCats.map((catOpt) => (
                  <option key={catOpt.slug} value={catOpt.slug}>
                    {catOpt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block mb-1 font-semibold">Tags (durch Komma getrennt)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            </div>

            {/* Inhalt */}
            <div>
              <label className="block mb-1 font-semibold">Inhalt</label>
              <ReactQuill value={content} onChange={setContent} className="bg-white" />
            </div>

            {/* Admin only: publish */}
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="publishCheck"
                  checked={isPublished}
                  onChange={() => setIsPublished(!isPublished)}
                />
                <label htmlFor="publishCheck" className="font-semibold">
                  Artikel veröffentlichen
                </label>
              </div>
            )}

            {/* Medien hochladen */}
            <div>
              <label className="block mb-1 font-semibold">Bilder/Videos hochladen</label>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleUploadMedia}
                className="w-full"
              />
            </div>

            {/* Vorschau + Entfernen */}
            {mediaPaths.length > 0 && (
              <motion.div
                className="bg-gray-50 p-2 rounded"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="font-semibold mb-2">Aktuelle Medien:</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {mediaPaths.map((mp, idx) => {
                    const isRelativePath = mp.startsWith('/uploads/');
                    const fullUrl = isRelativePath ? `${BASE_SERVER_URL}${mp}` : mp;

                    if (mp.match(/\.(mp4|mov|avi)$/i)) {
                      return (
                        <div key={idx} className="relative">
                          <video
                            src={fullUrl}
                            controls
                            className="w-full h-48 object-cover rounded shadow"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveMedia(mp)}
                            className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-sm"
                          >
                            Entfernen
                          </button>
                        </div>
                      );
                    } else {
                      return (
                        <div key={idx} className="relative">
                          <img
                            src={fullUrl}
                            alt={`media-${idx}`}
                            className="w-full h-48 object-cover rounded shadow"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveMedia(mp)}
                            className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-sm"
                          >
                            Entfernen
                          </button>
                        </div>
                      );
                    }
                  })}
                </div>
              </motion.div>
            )}

            {/* Speichern Button */}
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded hover:bg-orange-600 transition"
            >
              {id ? 'Artikel aktualisieren' : 'Artikel erstellen'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateEditArticle;
